import Backdrop from "components/global/Backdrop";
import ModalWrapper from "components/global/ModalWrapper";
import styled from "styled-components";
import webPromoImg from "assets/images/webpromimg-primary.png";
import Button from "components/global/Button";
import CloseIcon from "icons/CloseIcon";
import Spacer from "components/global/Spacer";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { addViewedPromo, getPromo } from "api";
import { useHistory } from "react-router-dom";

const Wrapper = styled(Backdrop)`
  .modalContainer {
    border-radius: 16px;
    background: ${(props) =>
      props.background === "primary" ? "#0d295a" : "#C2E3FF"};
    color: ${(props) => (props.background === "primary" ? "#fff" : "#000")};
    padding-bottom: 48px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: min(100%, 960px);
    margin: auto;
    height: auto;
    max-height: unset;

    @media (max-width: 768px) {
      padding: 16px;
      overflow-y: auto;
      max-height: 100vh;
    }

    .contentArea {
      display: flex;
      gap: 40px;
      width: 100%;

      @media (max-width: 768px) {
        flex-direction: column;
        gap: 24px;
        width: 100%;
        height: 100%;
      }
    }

    .closeBtn {
      width: 32px;
      padding: 0;
      border-radius: 50%;
      align-self: flex-end;
    }

    .imageContainer {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;

      @media (max-width: 768px) {
        display: none;
      }
    }

    .imageBox {
      display: flex;
      width: 100%;
      height: 400px;
      justify-content: center;
      align-items: center;
      border-radius: 8px;
      background: ${(props) =>
        props.background === "primary" ? "#0d295a" : "#C2E3FF"};

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 8px;
      }
    }

    .textContainer {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 48px;
      width: 100%;
      height: 400px;

      .textContent {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .imageBox {
        display: none;
      }

      @media (max-width: 768px) {
        width: 100%;
        height: 100%;

        .imageBox {
          display: block;
          width: 100%;
          height: 300px;
          border-radius: 8px;

          img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            border-radius: 8px;
          }
        }
      }
    }

    .buttonContainer {
      display: flex;
      gap: 16px;

      .btn {
        padding: 0 48px;
      }

      .secondaryBtn {
        border: ${(props) =>
          props.background === "primary" ? "1px solid #fff" : "1px solid #000"};
        color: ${(props) => (props.background === "primary" ? "#fff" : "#000")};
      }

      @media (max-width: 768px) {
        flex-direction: column;
        gap: 16px;
        width: 100%;

        .btn {
          width: 100%;
        }
      }
    }

    .slider {
      display: flex;
      align-items: center;
      align-self: center;

      .sliderDot {
        width: 4px;
        height: 4px;
        border-radius: 6px;
        background: #fff;
        margin: 0 4px;
        cursor: pointer;

        &.active {
          background: #fff;
          width: 16px;
        }
      }
    }
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;  

const WebPromoBox = () => {
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  const { data: promo } = useQuery({
    queryFn: getPromo,
    queryKey: ["get-promo"],
    select: (data) => data?.data?.data,
  });

  const { mutate: addViewedPromoMutation } = useMutation({
    mutationFn: (id) => addViewedPromo(id),
    mutationKey: ["add-viewed-promo", activeSlide],
  });

  const handleSlideChange = (index) => {
    setActiveSlide(index);
  };

  const activePromo = useMemo(() => promo?.[activeSlide], [promo, activeSlide]);

  const handlePrimaryButtonClick = () => {
    history.push(activePromo?.meta?.button_redirection);
    setShowModal(false);
    addViewedPromoMutation(activePromo?.notification_id);
  };

  const handleSecondaryButtonClick = () => {
    history.push(activePromo?.meta?.secondary_button?.button_redirection);
    setShowModal(false);
    addViewedPromoMutation(activePromo?.notification_id);
  };

  const handleClose = () => {
    setShowModal(false);
    addViewedPromoMutation(activePromo?.notification_id);
  }

  useEffect(() => {
    if (promo && promo.length > 0) {
      setShowModal(true);
    }
  }, [promo]);

  useEffect(() => {
    if (promo && promo.length > 1 && showModal) {
      const interval = setInterval(() => {
        setActiveSlide((prevSlide) => (prevSlide + 1) % promo.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [promo, showModal]);

  if (!showModal) return null;

  return (
    <Wrapper
      className="flexRow justifyEnd show"
      background={activePromo?.meta?.background_color}
    >
      <ModalWrapper className="modalContainer">
        <Button className="closeBtn" bg="var(--grey_1)" height="32px" onClick={handleClose} iconComponent={<CloseIcon color="#fff" size={16} />} />
        <div className="contentArea">
          <div className="imageContainer">
            <div className="imageBox">
              <img
                src={activePromo?.meta?.medias?.[0]}
                alt={activePromo?.name}
              />
            </div>
          </div>
          <div className="textContainer">
            <div className="textContent">
              <p className="l4 colorSecondary upperCase">{activePromo?.name}</p>
              <h4>{activePromo?.meta?.title}</h4>
              <Spacer yMobile={2} />
              <div className="imageBox">
                <img src={activePromo?.meta?.medias?.[0]} alt="" />
              </div>
              <Spacer yMobile={2} />
              <p
                dangerouslySetInnerHTML={{ __html: activePromo?.meta?.content }}
              ></p>
            </div>

            <div className="buttonContainer">
              <Button
                text={activePromo?.meta?.button_text}
                height="36px"
                fontSize="14px"
                className="btn"
                onClick={handlePrimaryButtonClick}
              />
              {activePromo?.meta?.secondary_button?.show === true && (
                <Button
                  text={activePromo?.meta?.secondary_button?.button_text}
                  height="36px"
                  className="btn borderedGrey secondaryBtn"
                  color="#fff"
                  fontSize="14px"
                  onClick={handleSecondaryButtonClick}
                />
              )}
            </div>
          </div>
        </div>
        <div className="slider">
          {promo?.map((_, index) => (
            <div
              key={index}
              className={`sliderDot ${index === activeSlide ? "active" : ""}`}
              onClick={() => handleSlideChange(index)}
            ></div>
          ))}
        </div>
      </ModalWrapper>
    </Wrapper>
  );
};

export default WebPromoBox;
